import React, { useEffect, useState, useMemo } from 'react';
import type { Cidade, Escola, CatalogoEscolaOnboarding } from '../types';
import { calc, calcTotal, buscarEscolasMunicipio, nomeRand, buscarEscolasOnboarding, ensureCatalogoPopulado, criarEscolaDemo } from '../types';
import { UnifiedMapView } from '../../developer-cockpit/components/sim-setup/UnifiedMapView';
import type { MapDataPoint } from '../../developer-cockpit/components/sim-setup/UnifiedMapView';

// Ícone de Escola Sutil e Nobre para a listagem/detalhes
const SchoolSvgIcon = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.95, flexShrink: 0 }}>
    <path d="M22 10v6M2 10l10-5 10 5-10 5-10 5z" />
    <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
    <path d="M12 22v-7" />
  </svg>
);

// Brasão de ouro executivo com pulso e brilho diamante
const GoldSealSvg = () => (
  <div style={{ 
    animation: 'corePulse 3.5s ease-in-out infinite',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative'
  }}>
    <svg width="78" height="78" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.98 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="url(#gold-grad-seal)" stroke="#fbbf24" strokeWidth="1.5" />
      <path d="M12 4.5 L13.8 9.1 L18.7 9.3 L14.9 12.4 L16.1 17.2 L12 14.5 L7.9 17.2 L9.2 12.4 L5.3 9.3 L10.2 9.1 Z" fill="#0f0b29" />
      
      {/* Brilho de Diamante/Ouro Fino e Côncavo reluzindo na ponta superior direita do Brasão */}
      <path d="M19 2 Q19 5 22 5 Q19 5 19 8 Q19 5 16 5 Q19 5 19 2 Z" fill="#ffffff" style={{
        animation: 'sparkle 2.8s ease-in-out infinite',
        transformOrigin: '19px 5px',
        filter: 'drop-shadow(0 0 4px #ffe066)'
      }} />

      <defs>
        <linearGradient id="gold-grad-seal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffe066" />
          <stop offset="50%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

export interface EscolaStepProps {
  cidade: Cidade;
  selecionados: Set<string>;
  onToggle: (e: Escola) => void;
  /** Pode receber ids explícitos (após criar do INEP) para evitar race do setState */
  onNext: (escolaIds?: string[]) => void;
  /** True enquanto o parent cria/inicia a sessão — desabilita o botão e muda o texto */
  launching?: boolean;
}

interface ProgressoState {
  ativo: boolean;
  nome: string;
  pct: number;
  logLines: string[];
  sucesso: boolean;
  finalizado: boolean;
  /** 'lote' = várias escolas INEP; 'unica' = seed de uma escola */
  modo?: 'unica' | 'lote';
  atual?: number;
  total?: number;
}

const NOMES_MASCULINOS = [
  'Carlos', 'Roberto', 'Antônio', 'Luiz', 'Marcos', 'Paulo', 'André', 'Fernando', 'Lucas', 'Mateus',
  'Felipe', 'Thiago', 'Pedro', 'João', 'Gabriel', 'Gustavo', 'Daniel', 'Marcelo', 'Júlio', 'Bruno'
];

const NOMES_FEMININOS = [
  'Maria', 'Ana', 'Cláudia', 'Juliana', 'Adriana', 'Patrícia', 'Camila', 'Beatriz', 'Fernanda', 'Aline',
  'Letícia', 'Amanda', 'Gabriela', 'Larissa', 'Mariana', 'Sofia', 'Isabela', 'Júlia', 'Luana', 'Renata'
];

const SOBRENOMES = [
  'Silva', 'Santos', 'Oliveira', 'Souza', 'Rodrigues', 'Ferreira', 'Alves', 'Pereira', 'Lima', 'Gomes',
  'Costa', 'Ribeiro', 'Martins', 'Carvalho', 'Rocha', 'Dias', 'Moreira', 'Teixeira', 'Pinto', 'Mendes'
];

const BAIRROS_EXEMPLO = [
  'JARDIM AMÉRICA', 'CENTRO', 'PLANALTO', 'SIDIL', 'BOM PASTOR', 'NITERÓI', 'SERRA VERDE', 
  'IPANEMA', 'SANTA CLARA', 'VILA BELVEDERE', 'SÃO JOSÉ', 'PORTO VELHO'
];

const PROFESSORES_EXEMPLO = [
  'Profª Helena Santos', 'Prof. Ricardo Gomes', 'Profª Cláudia Lima', 'Prof. Maurício Silva',
  'Profª Sandra Oliveira', 'Prof. Alexandre Martins', 'Profª Patrícia Costa', 'Prof. Marcos Rocha'
];

const TURMAS_EXEMPLO = [
  '1º ANO A', '2º ANO B', '3º ANO A', '4º ANO C', '5º ANO A', '6º ANO B', '7º ANO A', '8º ANO B', '9º ANO A'
];

const gerarAvatarSvg = (nome: string, corBg: string) => {
  const partes = nome.split(' ');
  const inicial1 = partes[0] ? partes[0][0] : '?';
  const inicial2 = partes[1] ? partes[1][0] : '';
  const iniciais = (inicial1 + inicial2).toUpperCase();
  
  return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="100%" height="100%" fill="${encodeURIComponent(corBg)}"/><text x="50%" y="55%" font-family="sans-serif" font-weight="bold" font-size="22" fill="%23ffffff" dominant-baseline="middle" text-anchor="middle">${iniciais}</text></svg>`;
};

const FASES = [
  { pct:  5, msg: '[1/10] Inicializando estrutura escolar...' },
  { pct: 14, msg: '[2/10] Criando turnos e grade curricular...' },
  { pct: 24, msg: '[3/10] Inserindo equipe e professores...' },
  { pct: 38, msg: '[4/10] Gerando fichas dos alunos...' },
  { pct: 50, msg: '[5/10] Cadastrando responsaveis e tags RFID...' },
  { pct: 62, msg: '[6/10] Gerando historico de presenca...' },
  { pct: 74, msg: '[7/10] Lancando avaliacoes e notas...' },
  { pct: 83, msg: '[8/10] Criando comunicados e incidentes...' },
  { pct: 91, msg: '[9/10] Finalizando configuracoes...' },
  { pct: 95, msg: '[10/10] Validando integridade dos dados...' },
];

export function EscolaStep({ cidade, selecionados, onToggle, onNext, launching = false }: EscolaStepProps) {
  const [escolas, setEscolas] = useState<Escola[]>([]);
  const [loading, setLoading] = useState(true);
  const [novaAlunos, setNovaAlunos] = useState(200);
  const [escolaAnos, setEscolaAnos] = useState(10);

  const [sugestoes, setSugestoes] = useState<CatalogoEscolaOnboarding[]>([]);
  const [sugestoesLoading, setSugestoesLoading] = useState(false);
  const [sugestoesSel, setSugestoesSel] = useState<Set<string>>(new Set());
  const [criandoSugestoes, setCriandoSugestoes] = useState(false);
  const [criandoIdx, setCriandoIdx] = useState(0);
  const [criandoTotal, setCriandoTotal] = useState(0);
  const [sugestaoStatus, setSugestaoStatus] = useState<Record<string, 'aguardando'|'criando'|'criado'|'erro'>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const [progresso, setProgresso] = useState<ProgressoState | null>(null);

  // Estados locais para geocodificação do município e escolas
  const [municipioLat, setMunicipioLat] = useState<number | null>(() => cidade.lat ? parseFloat(cidade.lat.toString()) : null);
  const [municipioLng, setMunicipioLng] = useState<number | null>(() => cidade.lng ? parseFloat(cidade.lng.toString()) : null);
  const [statusGeocoding, setStatusGeocoding] = useState<string>('');
  const [erroGeocoding, setErroGeocoding] = useState<string>('');

  // Escola selecionada no mapa para exibição no painel lateral
  const [focusedEscola, setFocusedEscola] = useState<Escola | null>(null);

  // Estados locais para interações de luxo nos botões
  const [nextHovered, setNextHovered] = useState(false);
  const [createHovered, setCreateHovered] = useState(false);

  // Nome do prefeito logado trazido do LocalStorage
  const gestorNome = useMemo(() => {
    return localStorage.getItem('sim_gestor_nome') || 'Prefeito Municipal';
  }, []);

  useEffect(() => {
    async function inicializarOnboarding() {
      setLoading(true);
      setErroGeocoding('');
      
      let lat = municipioLat;
      let lng = municipioLng;
      
      // 1. Geocodifica município se coordenadas estiverem zeradas ou nulas
      if (!lat || !lng || lat === 0 || lng === 0) {
        setStatusGeocoding('Localizando coordenadas do município...');
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(`${cidade.nome}, ${cidade.uf}, Brasil`)}&format=json&limit=1`,
            { headers: { 'User-Agent': 'AlunoConectado-Onboarding/1.0' } }
          );
          const json = await response.json();
          if (json && json.length > 0) {
            lat = parseFloat(json[0].lat);
            lng = parseFloat(json[0].lon);
            setMunicipioLat(lat);
            setMunicipioLng(lng);
          } else {
            setErroGeocoding(`Impossível determinar as coordenadas geográficas para ${cidade.nome} / ${cidade.uf}. Escolha outro município ou verifique se digitou o nome correto.`);
            setLoading(false);
            return;
          }
        } catch (err: any) {
          setErroGeocoding(`Falha na geolocalização de ${cidade.nome} / ${cidade.uf}. Verifique sua conexão de rede.`);
          setLoading(false);
          return;
        }
      }

      // 2. Busca as escolas da simulação
      setStatusGeocoding('Buscando unidades escolares no banco de dados...');
      try {
        const data = await buscarEscolasMunicipio(cidade.id);
        // Sem coord no banco: NÃO colapsar todas no centro da cidade —
        // espalha em anel determinístico (evita 10 pinos empilhados).
        const dataTratada = data.map((e, i) => {
          const hasLat = e.lat != null && Number(e.lat) !== 0 && Number.isFinite(Number(e.lat));
          const hasLng = e.lng != null && Number(e.lng) !== 0 && Number.isFinite(Number(e.lng));
          if (hasLat && hasLng) return { ...e, lat: Number(e.lat), lng: Number(e.lng) };
          if (lat == null || lng == null) return { ...e, lat: e.lat ?? null, lng: e.lng ?? null };
          const n = Math.max(data.length, 1);
          const angle = (2 * Math.PI * i) / n + 0.35;
          const distM = 900 + (i % 4) * 350; // 0.9–1.95 km
          const dLat = (distM / 111000) * Math.sin(angle);
          const dLng = (distM / (111000 * Math.cos(lat * Math.PI / 180))) * Math.cos(angle);
          return { ...e, lat: lat + dLat, lng: lng + dLng };
        });
        setEscolas(dataTratada);

        // 3. Se a rede estiver vazia, busca sugestões INEP e geocodifica se necessário
        if (dataTratada.length === 0) {
          setSugestoesLoading(true);
          setStatusGeocoding('Carregando sugestões escolares do censo INEP...');
          await ensureCatalogoPopulado();
          const found = await buscarEscolasOnboarding(cidade.nome, cidade.uf, 5, lat, lng);
          
          const foundTratado: any[] = [];
          for (let i = 0; i < found.length; i++) {
            const sug = found[i];
            if (!sug.latitude || !sug.longitude || sug.latitude === 0 || sug.longitude === 0) {
              setStatusGeocoding(`Mapeando endereço da escola: ${sug.nomeDemo.toUpperCase()}...`);
              try {
                // Nominatim Nominatim API com delay de 1s para respeitar rate limit
                await new Promise(r => setTimeout(r, 1000));
                const resGeo = await fetch(
                  `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(`${sug.nomeDemo}, ${cidade.nome}, ${cidade.uf}, Brasil`)}&format=json&limit=1`,
                  { headers: { 'User-Agent': 'AlunoConectado-Onboarding/1.0' } }
                );
                const jsonGeo = await resGeo.json();
                if (jsonGeo && jsonGeo.length > 0) {
                  sug.latitude = parseFloat(jsonGeo[0].lat);
                  sug.longitude = parseFloat(jsonGeo[0].lon);
                } else {
                  // Fallback: espalha levemente ao redor do centro da prefeitura
                  const offsetLat = (Math.random() - 0.5) * 0.008;
                  const offsetLng = (Math.random() - 0.5) * 0.008;
                  sug.latitude = lat! + offsetLat;
                  sug.longitude = lng! + offsetLng;
                }
              } catch {
                const offsetLat = (Math.random() - 0.5) * 0.008;
                const offsetLng = (Math.random() - 0.5) * 0.008;
                sug.latitude = lat! + offsetLat;
                sug.longitude = lng! + offsetLng;
              }
            }
            foundTratado.push(sug);
          }

          setSugestoes(foundTratado);
          setSugestoesSel(new Set(foundTratado.map(s => s.codigoInep)));
          setSugestoesLoading(false);
        }
      } catch {
        setErroGeocoding('Erro ao carregar os dados da simulação escolar.');
      }
      
      setStatusGeocoding('');
      setLoading(false);
    }

    inicializarOnboarding();
  }, [cidade.id, reloadKey]);

  const selecionadasObj = escolas.filter(e => selecionados.has(e.id));
  const totalResumo = calcTotal(selecionadasObj);
  const novaPreview = calc(novaAlunos);
  
  // A rede já tem escolas se o banco de dados contiver registros
  const temEscolasNoBanco = escolas.length > 0;
  const podeAvançarBanco = selecionados.size > 0;

  // Coordenadas brutas da cidade (podem divergir do cluster escolar INEP/DB)
  const cidadeLat = municipioLat ?? -20.0754;
  const cidadeLng = municipioLng ?? -44.5775;
  // Fallback para criação de escola demo quando ainda não há cluster
  const prefeituraLat = cidadeLat;
  const prefeituraLng = cidadeLng;

  // Mapeia todas as escolas e gera residências, alunos e responsáveis fictícios dinamicamente orbitando as escolas selecionadas
  const dataPointsForMap = useMemo((): MapDataPoint[] => {
    const pts: MapDataPoint[] = [];

    // Função auxiliar pseudo-aleatória determinística baseada no id da escola para não mudar de posição
    const stringHash = (str: string) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return Math.abs(hash);
    };

    // Mapeia todas as escolas disponíveis
    const todasEscolas = [...escolas];
    if (escolas.length === 0 && sugestoes.length > 0) {
      sugestoes.forEach(s => {
        if (s.latitude && s.longitude) {
          todasEscolas.push({
            id: 'inep-' + s.codigoInep,
            nome: s.nomeDemo,
            alunos: s.nAlunos,
            rede: 'MUNICIPAL',
            isDemo: true,
            municipioId: cidade.id,
            lat: s.latitude,
            lng: s.longitude
          } as any);
        }
      });
    }

    // Ancora a prefeitura perto do centroid das escolas (coord da cidade às vezes fica longe).
    let prefLat = cidadeLat;
    let prefLng = cidadeLng;
    const escolasComCoord = todasEscolas.filter(e => {
      const la = e.lat != null ? parseFloat(String(e.lat)) : NaN;
      const lo = e.lng != null ? parseFloat(String(e.lng)) : NaN;
      return Number.isFinite(la) && Number.isFinite(lo) && la !== 0 && lo !== 0;
    });
    if (escolasComCoord.length > 0) {
      const cLat = escolasComCoord.reduce((s, e) => s + parseFloat(String(e.lat)), 0) / escolasComCoord.length;
      const cLng = escolasComCoord.reduce((s, e) => s + parseFloat(String(e.lng)), 0) / escolasComCoord.length;
      const hash = stringHash(`prefeitura-${cidade.id}`);
      const angle = ((hash % 360) / 360) * 2 * Math.PI;
      const distMetros = 180 + (hash % 120); // ~180–300 m do centro das escolas
      prefLat = cLat + (distMetros / 111000) * Math.sin(angle);
      prefLng = cLng + (distMetros / (111000 * Math.cos(cLat * Math.PI / 180))) * Math.cos(angle);
    }

    pts.push({
      id: 'prefeitura',
      type: 'PREFEITURA',
      lat: prefLat,
      lng: prefLng,
      label: `Prefeitura de ${cidade.nome}`,
      sublabel: `Gabinete de Alta Autoridade`
    });

    // Distância mínima entre escolas (e vs prefeitura). Se todas caem no mesmo lat/lng
    // (fallback da cidade), a checagem só vs prefeitura NÃO basta — elas ficam empilhadas.
    const MIN_SEP_M = 320;
    const metersBetween = (aLat: number, aLng: number, bLat: number, bLng: number) => {
      const dx = (aLat - bLat) * 111000;
      const dy = (aLng - bLng) * 111000 * Math.cos(((aLat + bLat) / 2) * Math.PI / 180);
      return Math.sqrt(dx * dx + dy * dy);
    };
    const ringSlot = (index: number, total: number, hash: number) => {
      const angle = (2 * Math.PI * index) / Math.max(total, 1) + ((hash % 47) / 47) * 0.4;
      const distMetros = 850 + (index % 5) * 280 + (hash % 90); // ~0.85–2.1 km
      const lat = cidadeLat + (distMetros / 111000) * Math.sin(angle);
      const lng = cidadeLng + (distMetros / (111000 * Math.cos(cidadeLat * Math.PI / 180))) * Math.cos(angle);
      return { lat, lng };
    };

    type SchoolPos = { e: typeof todasEscolas[0]; lat: number; lng: number; index: number };
    const schoolPositions: SchoolPos[] = [];
    todasEscolas.forEach((e, index) => {
      if (e.lat == null || e.lng == null) return;
      const latVal = parseFloat(String(e.lat));
      const lngVal = parseFloat(String(e.lng));
      if (!Number.isFinite(latVal) || !Number.isFinite(lngVal) || latVal === 0 || lngVal === 0) return;
      schoolPositions.push({ e, lat: latVal, lng: lngVal, index });
    });

    // Se o grosso das escolas está amontoado (< MIN_SEP), redistribui o grupo inteiro em anel.
    let stackedCluster = false;
    if (schoolPositions.length >= 2) {
      let closePairs = 0;
      let checks = 0;
      for (let i = 0; i < schoolPositions.length; i++) {
        for (let j = i + 1; j < schoolPositions.length; j++) {
          checks++;
          if (metersBetween(schoolPositions[i].lat, schoolPositions[i].lng, schoolPositions[j].lat, schoolPositions[j].lng) < MIN_SEP_M) {
            closePairs++;
          }
        }
      }
      stackedCluster = checks > 0 && closePairs / checks >= 0.35;
    }

    const placed: { lat: number; lng: number }[] = [{ lat: prefLat, lng: prefLng }];
    const totalSchools = schoolPositions.length;

    schoolPositions.forEach((sp, i) => {
      let latVal = sp.lat;
      let lngVal = sp.lng;
      const hash = stringHash(sp.e.id);

      const tooCloseToPlaced = () =>
        placed.some(p => metersBetween(latVal, lngVal, p.lat, p.lng) < MIN_SEP_M);

      if (stackedCluster || tooCloseToPlaced()) {
        // Tenta slots em anel até achar folga (determinístico)
        for (let attempt = 0; attempt < Math.max(totalSchools * 2, 8); attempt++) {
          const slot = ringSlot(i + attempt, totalSchools, hash + attempt * 17);
          latVal = slot.lat;
          lngVal = slot.lng;
          if (!tooCloseToPlaced()) break;
        }
        sp.e.lat = latVal;
        sp.e.lng = lngVal;
      }

      placed.push({ lat: latVal, lng: lngVal });

      const isSel = selecionados.has(sp.e.id) || (escolas.length === 0 && sugestoesSel.has(sp.e.id.replace('inep-', '')));

      pts.push({
        id: sp.e.id,
        type: 'SCHOOL',
        lat: latVal,
        lng: lngVal,
        label: sp.e.nome,
        sublabel: `Rede ${sp.e.rede} • ${sp.e.alunos} alunos • ${isSel ? '✓ Incluída na Simulação' : 'Não selecionada'}`
      });

      // Reutiliza lat/lng dispersos no bloco de residências abaixo
      sp.lat = latVal;
      sp.lng = lngVal;
    });

    schoolPositions.forEach(sp => {
      const e = sp.e;
      const latVal = sp.lat;
      const lngVal = sp.lng;
      const isSel = selecionados.has(e.id) || (escolas.length === 0 && sugestoesSel.has(e.id.replace('inep-', '')));

      // Se a escola estiver selecionada, espalha residências, alunos e responsáveis ao redor de forma determinística
      if (isSel) {
        const hash = stringHash(e.id);

        // Inicializador de LCG sementeado para gerar floats determinísticos entre 0 e 1
        let seed = hash;
        const seededRandom = () => {
          seed = (seed * 1664525 + 1013904223) % 4294967296;
          return seed / 4294967296;
        };

        const totalAlunosReal = e.alunos || 100;
        const totalResidenciaReal = totalAlunosReal;

        // Distribuição trigonométrica polar em círculo de raio ~1.4km
        const maxRadius = 1400 / 111000; // ~0.0126 graus (1.4km)
        const minRadius = 100 / 111000;  // ~0.0009 graus (100m)

        // Lista de casas aceitas para detecção de colisão
        const acceptedHomes: { lat: number; lng: number }[] = [];

        for (let i = 0; i < totalResidenciaReal; i++) {
          let resLat = latVal;
          let resLng = lngVal;
          let foundLocation = false;

          // Algoritmo de não-colisão de 35 metros (máximo 100 tentativas para não travar a CPU)
          for (let attempt = 0; attempt < 100; attempt++) {
            const r = minRadius + (maxRadius - minRadius) * Math.sqrt(seededRandom());
            const theta = seededRandom() * 2 * Math.PI;

            const offsetLat = r * Math.sin(theta);
            const offsetLng = r * Math.cos(theta) / Math.cos(latVal * Math.PI / 180);

            const testLat = latVal + offsetLat;
            const testLng = lngVal + offsetLng;

            // Checa proximidade com as casas já alocadas
            let hasCollision = false;
            for (const home of acceptedHomes) {
              const dx = (testLat - home.lat) * 111000;
              const dy = (testLng - home.lng) * 111000 * Math.cos(latVal * Math.PI / 180);
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < 35) { // 35 metros de margem/folga
                hasCollision = true;
                break;
              }
            }

            if (!hasCollision) {
              resLat = testLat;
              resLng = testLng;
              foundLocation = true;
              break;
            }
          }

          // Se nenhuma coordenada sem colisão for achada, aceita a última testada para evitar travamento
          if (!foundLocation) {
            const r = minRadius + (maxRadius - minRadius) * Math.sqrt(seededRandom());
            const theta = seededRandom() * 2 * Math.PI;
            resLat = latVal + r * Math.sin(theta);
            resLng = lngVal + (r * Math.cos(theta)) / Math.cos(latVal * Math.PI / 180);
          }

          acceptedHomes.push({ lat: resLat, lng: resLng });

          // Determina a quantidade de membros da família (2 ou 3)
          const temSegundoResponsavel = seededRandom() < 0.8;
          const numMembros = temSegundoResponsavel ? 3 : 2;

          // Dados determinísticos da família
          const bairro = BAIRROS_EXEMPLO[(hash + i) % BAIRROS_EXEMPLO.length];
          const turma = TURMAS_EXEMPLO[(hash + i * 3) % TURMAS_EXEMPLO.length];
          const prof1 = PROFESSORES_EXEMPLO[(hash + i * 7) % PROFESSORES_EXEMPLO.length];
          const prof2 = PROFESSORES_EXEMPLO[(hash + i * 11) % PROFESSORES_EXEMPLO.length];
          const professores = prof1 === prof2 ? [prof1] : [prof1, prof2];

          // Sobrenome da família coerente
          const sobrenomeFam = SOBRENOMES[(hash + i * 13) % SOBRENOMES.length];

          // Responsável 1
          const resp1Genero = seededRandom() < 0.5;
          const resp1NomeBase = resp1Genero 
            ? NOMES_MASCULINOS[(hash + i * 17) % NOMES_MASCULINOS.length]
            : NOMES_FEMININOS[(hash + i * 19) % NOMES_FEMININOS.length];
          const nomeResp1 = `${resp1NomeBase} ${sobrenomeFam}`;
          const temApp1 = seededRandom() < 0.65; // 65% tem app instalado
          const avatarResp1 = gerarAvatarSvg(nomeResp1, '#7c3aed');

          // Responsável 2
          let nomeResp2 = '';
          let temApp2 = false;
          let avatarResp2 = '';
          if (temSegundoResponsavel) {
            const resp2Genero = !resp1Genero; // Gêneros opostos para diversidade
            const resp2NomeBase = resp2Genero
              ? NOMES_MASCULINOS[(hash + i * 29) % NOMES_MASCULINOS.length]
              : NOMES_FEMININOS[(hash + i * 31) % NOMES_FEMININOS.length];
            nomeResp2 = `${resp2NomeBase} ${sobrenomeFam}`;
            temApp2 = seededRandom() < 0.55;
            avatarResp2 = gerarAvatarSvg(nomeResp2, '#3b82f6');
          }

          // Aluno
          const alunoGenero = seededRandom() < 0.5;
          const alunoNomeBase = alunoGenero
            ? NOMES_MASCULINOS[(hash + i * 37) % NOMES_MASCULINOS.length]
            : NOMES_FEMININOS[(hash + i * 41) % NOMES_FEMININOS.length];
          const nomeAluno = `${alunoNomeBase} ${sobrenomeFam}`;
          const temRfid = seededRandom() < 0.85; // 85% tem chaveiro RFID
          const avatarAluno = gerarAvatarSvg(nomeAluno, '#10b981');

          // Consolidação do Payload da Família
          const familiaPayload = {
            idHome: `${e.id}-home-${i}`,
            bairro,
            turma,
            professores,
            escolaNome: e.nome,
            aluno: {
              nome: nomeAluno,
              avatar: avatarAluno,
              temRfid
            },
            responsavel1: {
              nome: nomeResp1,
              avatar: avatarResp1,
              temApp: temApp1
            },
            responsavel2: temSegundoResponsavel ? {
              nome: nomeResp2,
              avatar: avatarResp2,
              temApp: temApp2
            } : null
          };

          // 1. Adiciona a Residência
          pts.push({
            id: `${e.id}-home-${i}`,
            type: 'STUDENT_HOME',
            lat: resLat,
            lng: resLng,
            label: `Residência de ${nomeAluno}`,
            sublabel: `Bairro: ${bairro} • Zona de Transporte`,
            payload: {
              familia: familiaPayload,
              temApp: temApp1 || temApp2,
              temRfid
            }
          });

          // Determina a quantidade de membros da família (2 ou 3)
          const getMemberCoords = (indexInHome: number) => {
            const d = 10 / 111000; // 10 metros de distância do centro (distribuídos geometricamente dentro do quadrado de 30m)
            let angle = 0;
            if (numMembros === 3) {
              angle = (indexInHome * 2 * Math.PI) / 3; // 0, 120, 240 graus (triângulo equilátero)
            } else {
              angle = indexInHome * Math.PI; // 0, 180 graus (linha reta simétrica)
            }
            const mLat = resLat + d * Math.sin(angle);
            const mLng = resLng + (d * Math.cos(angle)) / Math.cos(resLat * Math.PI / 180);
            return { lat: mLat, lng: mLng };
          };

          // 2. Adiciona o Aluno (membro 0)
          const coordsAluno = getMemberCoords(0);
          pts.push({
            id: `${e.id}-student-${i}`,
            type: 'STUDENT',
            lat: coordsAluno.lat,
            lng: coordsAluno.lng,
            label: `Aluno(a): ${nomeAluno}`,
            sublabel: `Turma: ${turma} • Chaveiro RFID: ${temRfid ? 'Ativo' : 'Inativo'}`,
            payload: {
              familia: familiaPayload,
              temApp: temApp1 || temApp2,
              temRfid
            }
          });

          // 3. Adiciona o Responsável 1 (membro 1)
          const coordsGuardian1 = getMemberCoords(1);
          pts.push({
            id: `${e.id}-guardian-${i}-1`,
            type: 'GUARDIAN',
            lat: coordsGuardian1.lat,
            lng: coordsGuardian1.lng,
            label: `Responsável: ${nomeResp1}`,
            sublabel: `Grau de Parentesco • App AC: ${temApp1 ? 'Instalado' : 'Não Instalado'}`,
            payload: {
              familia: familiaPayload,
              temApp: temApp1 || temApp2,
              temRfid
            }
          });

          // 4. Adiciona o Responsável 2 opcional (membro 2)
          if (temSegundoResponsavel) {
            const coordsGuardian2 = getMemberCoords(2);
            pts.push({
              id: `${e.id}-guardian-${i}-2`,
              type: 'GUARDIAN',
              lat: coordsGuardian2.lat,
              lng: coordsGuardian2.lng,
              label: `Responsável: ${nomeResp2}`,
              sublabel: `Grau de Parentesco • App AC: ${temApp2 ? 'Instalado' : 'Não Instalado'}`,
              payload: {
                familia: familiaPayload,
                temApp: temApp1 || temApp2,
                temRfid
              }
            });
          }
        }
      }
    });

    return pts;
  }, [escolas, selecionados, sugestoes, sugestoesSel, cidadeLat, cidadeLng, cidade.nome, cidade.id]);

  // Evento de clique no marcador do mapa para carregar o painel lateral de detalhes
  const handleMarkerClick = (point: MapDataPoint) => {
    if (point.type === 'SCHOOL') {
      // Localiza o objeto Escola correspondente
      const found = escolas.find(e => e.id === point.id);
      if (found) {
        setFocusedEscola(found);
      } else {
        // Se for sugestão INEP ainda não criada
        const inepCode = point.id.replace('inep-', '');
        const sug = sugestoes.find(s => s.codigoInep === inepCode);
        if (sug) {
          setFocusedEscola({
            id: point.id,
            nome: sug.nomeDemo,
            alunos: sug.nAlunos,
            rede: 'MUNICIPAL',
            isDemo: true,
            municipioId: cidade.id,
            lat: sug.latitude ?? null,
            lng: sug.longitude ?? null
          });
        }
      }
    }
  };

  // Cria escola demo via backend com card de progresso animado
  const handleCriarNova = async () => {
    const nome = nomeRand();
    const totalEstimado = Math.max(8000, novaAlunos * 72);
    const intervalMs = Math.max(900, totalEstimado / FASES.length);

    setProgresso({ ativo: true, nome, pct: 0, logLines: ['⚡  Iniciando seed da escola demo...'], sucesso: false, finalizado: false });

    let faseIdx = 0;
    const timer = setInterval(() => {
      if (faseIdx >= FASES.length) return;
      const fase = FASES[faseIdx++];
      setProgresso(prev => prev ? { ...prev, pct: fase.pct, logLines: [...prev.logLines, fase.msg].slice(-20) } : prev);
    }, intervalMs);

    try {
      const diasEstimados = Math.min(30, Math.max(3, Math.round(escolaAnos * 1.5)));
      
      const result = await criarEscolaDemo({ 
        nome, 
        municipioId: cidade.id, 
        cidade: cidade.nome, 
        uf: cidade.uf, 
        nAlunos: novaAlunos,
        diasHistorico: diasEstimados 
      });
      clearInterval(timer);
      setProgresso(prev => prev ? { ...prev, pct: 100, logLines: [...prev.logLines, '✅  Escola criada com sucesso!'], finalizado: true, sucesso: true } : prev);

      const novaId = (result?.escola?.id) ? result.escola.id : ('nova-' + Date.now());
      const nova: Escola = {
        id: novaId,
        nome,
        alunos: result?.escola?.totalAlunos ?? novaAlunos,
        rede: 'MUNICIPAL',
        isDemo: true,
        municipioId: cidade.id,
        lat: (result?.escola as any)?.latitude ?? (result?.escola as any)?.lat ?? prefeituraLat,
        lng: (result?.escola as any)?.longitude ?? (result?.escola as any)?.lng ?? prefeituraLng
      };

      setTimeout(() => {
        setProgresso(null);
        setEscolas(prev => [nova, ...prev]);
        onToggle(nova);
        setReloadKey(k => k + 1);
      }, 2500);

    } catch {
      clearInterval(timer);
      setProgresso(prev => prev ? { ...prev, pct: 100, logLines: [...prev.logLines, '❌  Erro ao criar escola. Tente novamente.'], finalizado: true, sucesso: false } : prev);
      setTimeout(() => setProgresso(null), 3000);
    }
  };

  const handleCriarDoInep = async () => {
    const selecionadas = sugestoes.filter(s => sugestoesSel.has(s.codigoInep));
    if (!selecionadas.length) return;
    setCriandoSugestoes(true);
    setCriandoTotal(selecionadas.length);
    setCriandoIdx(0);

    // Mesmo overlay do seed unitário — antes só o botão mudava de texto
    setProgresso({
      ativo: true,
      modo: 'lote',
      nome: `${selecionadas.length} escolas em ${cidade.nome}`,
      pct: 0,
      atual: 0,
      total: selecionadas.length,
      logLines: [
        `⚡ Criando ${selecionadas.length} escola(s) demo a partir do catálogo INEP…`,
        `📍 ${cidade.nome} / ${cidade.uf}`,
      ],
      sucesso: false,
      finalizado: false,
    });

    const createdIds: string[] = [];
    for (let i = 0; i < selecionadas.length; i++) {
      const s = selecionadas[i];
      setCriandoIdx(i + 1);
      setSugestaoStatus(prev => ({ ...prev, [s.codigoInep]: 'criando' }));
      const pctInicio = Math.round((i / selecionadas.length) * 100);
      setProgresso(prev => prev ? {
        ...prev,
        modo: 'lote',
        nome: s.nomeDemo,
        atual: i + 1,
        total: selecionadas.length,
        pct: pctInicio,
        logLines: [
          ...prev.logLines,
          `🏫 [${i + 1}/${selecionadas.length}] ${s.nomeDemo} (${s.nAlunos} alunos)…`,
        ].slice(-24),
      } : prev);

      try {
        const result = await criarEscolaDemo({
          nome: s.nomeDemo,
          municipioId: cidade.id,
          cidade: cidade.nome,
          uf: cidade.uf,
          nAlunos: s.nAlunos,
        });
        const inepId = (result?.escola?.id) ? result.escola.id : ('inep-' + s.codigoInep);
        const nova: Escola = {
          id: inepId,
          nome: s.nomeDemo,
          alunos: result?.escola?.totalAlunos ?? s.nAlunos,
          rede: 'MUNICIPAL',
          isDemo: true,
          municipioId: cidade.id,
          lat: s.latitude ?? null,
          lng: s.longitude ?? null,
        };
        createdIds.push(inepId);
        onToggle(nova);
        setSugestaoStatus(prev => ({ ...prev, [s.codigoInep]: 'criado' }));
        const pctFim = Math.round(((i + 1) / selecionadas.length) * 100);
        setProgresso(prev => prev ? {
          ...prev,
          pct: pctFim,
          atual: i + 1,
          logLines: [...prev.logLines, `✅ ${s.nomeDemo}`].slice(-24),
        } : prev);
      } catch {
        setSugestaoStatus(prev => ({ ...prev, [s.codigoInep]: 'erro' }));
        setProgresso(prev => prev ? {
          ...prev,
          logLines: [...prev.logLines, `❌ Falha: ${s.nomeDemo}`].slice(-24),
        } : prev);
      }
    }

    const ok = createdIds.length > 0;
    setProgresso(prev => prev ? {
      ...prev,
      pct: 100,
      finalizado: true,
      sucesso: ok,
      nome: ok
        ? `${createdIds.length}/${selecionadas.length} escolas criadas`
        : 'Nenhuma escola criada',
      logLines: [
        ...(prev?.logLines ?? []),
        ok
          ? `✅ Lote concluído — iniciando simulação…`
          : `❌ Nenhuma escola foi criada. Tente novamente.`,
      ].slice(-24),
    } : prev);

    await new Promise((r) => setTimeout(r, ok ? 900 : 2200));
    setProgresso(null);
    setCriandoSugestoes(false);
    if (!ok) return;
    onNext(createdIds);
  };

  // O botão superior direito é o ÚNICO botão de ação de prosseguimento da tela.
  const handleAcaoPrincipal = () => {
    if (temEscolasNoBanco) {
      onNext();
    } else {
      handleCriarDoInep();
    }
  };

  // Define dinamicamente o texto e estado habilitado do único botão verde
  const textoBotaoPrincipal = launching
    ? 'INICIANDO…'
    : criandoSugestoes
      ? 'CRIANDO ESCOLAS…'
      : 'INICIAR SIMULAÇÃO';

  const botaoHabilitado = !launching && !criandoSugestoes && (
    temEscolasNoBanco
      ? podeAvançarBanco
      : sugestoesSel.size > 0
  );

  if (erroGeocoding) {
    return (
      <div style={{ display:'flex',flexDirection:'column',gap:24, padding: '20px 0', fontFamily: 'sans-serif' }}>
        <div>
          <div style={{ fontSize:11,fontWeight:900,color:'#f87171',letterSpacing:'0.2em',textTransform:'uppercase',marginBottom:6 }}>
            PASSO 3 DE 4 — FALHA DE GEOLOCALIZAÇÃO
          </div>
          <h1 style={{ fontSize:22,fontWeight:900,color:'#f87171',letterSpacing:'0.04em',textTransform:'uppercase',lineHeight:1.2,margin:0 }}>
            {cidade.nome} / {cidade.uf} Não Localizada
          </h1>
        </div>
        <div style={{ 
          display:'flex',
          flexDirection: 'column',
          alignItems:'center',
          justifyContent:'center',
          padding:'40px 30px',
          color:'#f87171',
          gap:16, 
          background:'rgba(248,113,113,0.05)', 
          borderRadius:16, 
          border:'1.5px dashed rgba(248,113,113,0.3)',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '32px' }}>🚨</span>
          <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.5, color: '#fca5a5', maxWidth: 460 }}>
            {erroGeocoding}
          </span>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
            Clique no botão de voltar <b>( ‹ )</b> no canto superior esquerdo para selecionar outro município da simulação.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display:'flex',flexDirection:'column',gap:24, padding: '20px 0', fontFamily: 'sans-serif' }}>
        <div>
          <div style={{ fontSize:11,fontWeight:900,color:'#fbbf24',letterSpacing:'0.2em',textTransform:'uppercase',marginBottom:6 }}>
            PASSO 3 DE 4 — CONFIGURAÇÃO DA REDE
          </div>
          <h1 style={{ fontSize:22,fontWeight:400,color:'rgba(255,255,255,0.85)',letterSpacing:'0.04em',textTransform:'uppercase',lineHeight:1.2,margin:0 }}>
            Carregando Escolas em {cidade.nome}
          </h1>
        </div>
        <div style={{ 
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          padding:'80px 40px',
          color:'#fbbf24',
          gap:14, 
          background:'rgba(255,255,255,0.02)', 
          borderRadius:16, 
          border:'1px solid rgba(255,255,255,0.05)' 
        }}>
          <span style={{ 
            width:18,
            height:18,
            borderRadius:'50%',
            border:'2px solid rgba(251,191,36,0.3)',
            borderTopColor:'#fbbf24',
            display:'inline-block',
            animation:'spin 0.75s linear infinite' 
          }} />
          <span style={{ fontSize:13, fontWeight:700, letterSpacing:'0.02em', color: '#fef08a' }}>
            {statusGeocoding || 'Buscando unidades escolares no banco de dados...'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display:'flex', 
      flexDirection:'column', 
      gap:10, 
      width:'100%', 
      height: '100%',
      flex: 1,
      minHeight: 0,
      background: 'transparent',
      borderRadius: '16px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        /* Scrollbar customizada premium */
        .premium-scroll::-webkit-scrollbar {
          width: 5px;
        }
        .premium-scroll::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.01);
          border-radius: 3px;
        }
        .premium-scroll::-webkit-scrollbar-thumb {
          background: rgba(251, 191, 36, 0.2);
          border-radius: 3px;
        }
        .premium-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(251, 191, 36, 0.45);
        }
        @keyframes pulse3d-green {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 18px rgba(16, 185, 129, 0.45), inset 0 1px 0 rgba(255,255,255,0.45);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 10px 32px rgba(16, 185, 129, 0.75), inset 0 1px 0 rgba(255,255,255,0.6);
          }
        }
        @keyframes corePulse {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 6px 14px rgba(245,158,11,0.4)) drop-shadow(0 0 10px rgba(251,191,36,0.15));
          }
          50% {
            transform: scale(1.05);
            filter: drop-shadow(0 12px 28px rgba(245,158,11,0.85)) drop-shadow(0 0 25px rgba(251,191,36,0.4));
          }
        }
        @keyframes sparkle {
          0%, 100% {
            transform: scale(0.3) rotate(0deg);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.2) rotate(90deg);
            opacity: 1;
          }
        }
      `}</style>

      {/* Header Premium Compacto com o Nome do Prefeito massageador de ego */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', gap:20, zIndex: 2, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <GoldSealSvg />
          <div>
            <div style={{ fontSize:10,fontWeight:900,color:'#fbbf24',letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:6, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
              CHEFE DO EXECUTIVO — REDE DE ENSINO MUNICIPAL
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h1 style={{ 
                fontSize: 18, 
                fontWeight: 900,
                color: '#ffffff',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1.2,
                margin: 0
              }}>
                {gestorNome}
              </h1>
              <div style={{
                display: 'inline-block',
                width: 'fit-content',
                fontSize: 12,
                fontWeight: 900,
                padding: '4px 12px',
                borderRadius: '8px',
                border: '1.2px solid transparent', // Borda transparente conforme diretriz
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                backgroundImage: 'linear-gradient(135deg, #ffe066 0%, #fbbf24 60%, #f59e0b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 2px 4px rgba(251, 191, 36, 0.35))',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                lineHeight: 1.1,
                boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.2)'
              }}>
                {cidade.nome} / {cidade.uf}
              </div>
            </div>
          </div>
        </div>

        {/* Único Botão de Lançamento Verde Pulsante (Iniciar Simulação) */}
        <button disabled={!botaoHabilitado} onClick={handleAcaoPrincipal}
          onMouseEnter={() => setNextHovered(true)}
          onMouseLeave={() => setNextHovered(false)}
          style={{
            padding:'12px 28px',
            borderRadius:12,
            border:'none',
            borderTop:'1.5px solid rgba(255,255,255,0.45)',
            borderBottom: nextHovered && botaoHabilitado ? '8px solid #047857' : '6px solid #047857', // Verde
            cursor: botaoHabilitado ? 'pointer' : 'not-allowed',
            background: botaoHabilitado
              ? (nextHovered ? 'linear-gradient(135deg, #34d399, #059669)' : 'linear-gradient(135deg, #10b981, #059669)') // Verde
              : 'rgba(255,255,255,0.06)',
            color: botaoHabilitado ? '#fff' : '#475569',
            fontSize:14,
            fontWeight:900,
            textTransform:'uppercase',
            letterSpacing:'0.06em',
            flexShrink:0,
            transform: nextHovered && botaoHabilitado ? 'translateY(-3px)' : 'translateY(0)',
            boxShadow: botaoHabilitado
              ? (nextHovered ? '0 14px 28px rgba(16, 185, 129, 0.45), 0 0 35px rgba(16, 185, 129, 0.25)' : '0 4px 18px rgba(16, 185, 129, 0.4)')
              : 'none',
            transition:'all 0.18s cubic-bezier(0.2, 0.8, 0.2, 1)', 
            opacity: criandoSugestoes ? 0.5 : 1,
            position: 'relative',
            overflow: 'hidden',
            animation: botaoHabilitado && !criandoSugestoes ? 'pulse3d-green 2.6s ease-in-out infinite' : 'none'
          }}
        >
          {/* Shimmer rápido interno no hover */}
          {nextHovered && botaoHabilitado && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45) 50%, transparent)',
              animation: 'goldSweep 1.6s ease-in-out infinite',
              pointerEvents: 'none',
              zIndex: 1
            }} />
          )}
          <span style={{ zIndex: 2 }}>{textoBotaoPrincipal}</span>
        </button>
      </div>

      {/* Linha Divisória Dourada em Gradiente */}
      <div style={{ 
        height: '2px', 
        width: '100%', 
        background: 'linear-gradient(to right, #ffe066 0%, #fbbf24 25%, rgba(245, 158, 11, 0.2) 75%, transparent 100%)',
        marginTop: -8,
        borderRadius: 2,
        zIndex: 2,
        flexShrink: 0,
      }} />

      {/* Layout com Mapa em Tela Cheia e Painel Lateral de Detalhes da Escola (Flyout) */}
      <div style={{ 
        display: 'flex', 
        width: '100%', 
        flex: '1 1 auto',
        minHeight: 0,
        height: '100%',
        borderRadius: 8, 
        border: '1.5px solid rgba(251, 191, 36, 0.35)', 
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
        overflow: 'hidden', 
        position: 'relative',
        zIndex: 2
      }}>
        
        {/* Painel Lateral de Detalhes da Escola (Flyout) à Esquerda - Oculto/Visível por clique */}
        <div style={{
          width: focusedEscola ? '330px' : '0px',
          opacity: focusedEscola ? 1 : 0,
          background: 'linear-gradient(135deg, #121528 0%, #0a0c18 100%)', // Painel escuro nobre metálico
          borderRight: focusedEscola ? '1.5px solid rgba(251, 191, 36, 0.35)' : 'none',
          padding: focusedEscola ? '20px' : '0px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          transition: 'all 0.3s cubic-bezier(0.25, 1, 0.5, 1)',
          overflow: 'hidden',
          zIndex: 5,
          position: 'relative',
          boxSizing: 'border-box'
        }}>
          {focusedEscola && (
            <>
              {/* Header do Painel */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontSize: 9, fontWeight: 900, color: '#fbbf24', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Ficha Cadastral da Unidade
                </span>
                <button 
                  onClick={() => setFocusedEscola(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 700,
                    padding: '2px 6px',
                    marginLeft: 'auto'
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
                >
                  ✕
                </button>
              </div>

              {/* Título da Escola e Tags Douradas/Pratas */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <h3 style={{ fontSize: 15, fontWeight: 900, color: '#f1f5f9', margin: 0, textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1.3 }}>
                  {focusedEscola.nome}
                </h3>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 8.5, fontWeight: 900, color: '#fbbf24', padding: '2px 6px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 4 }}>
                    REDE {focusedEscola.rede}
                  </span>
                  {focusedEscola.isDemo && (
                    <span style={{ fontSize: 8.5, fontWeight: 900, color: '#94a3b8', padding: '2px 6px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4 }}>
                      DEMO
                    </span>
                  )}
                </div>
              </div>

              {/* Status de Seleção */}
              {(() => {
                const isSel = selecionados.has(focusedEscola.id) || (escolas.length === 0 && sugestoesSel.has(focusedEscola.id.replace('inep-', '')));
                return (
                  <div style={{ 
                    padding: '10px 12px', 
                    borderRadius: 4, 
                    background: isSel ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.02)', 
                    border: isSel ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(255,255,255,0.08)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: isSel ? '#4ade80' : 'rgba(255,255,255,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    <span>{isSel ? '✓' : '·'}</span>
                    {isSel ? 'INCLUÍDA NA SIMULAÇÃO ATIVA' : 'EXCLUÍDA DA SIMULAÇÃO'}
                  </div>
                );
              })()}

              {/* Grid de Estatísticas em Azul Metálico com Contorno Dourado */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { l: 'Alunos', v: focusedEscola.alunos, c: '#ffe066' },
                  { l: 'Turmas', v: calc(focusedEscola.alunos).turmas, c: '#fbbf24' },
                  { l: 'Professores', v: calc(focusedEscola.alunos).profs, c: '#ffe066' },
                  { l: 'Responsáveis', v: calc(focusedEscola.alunos).resps, c: '#fbbf24' }
                ].map(x => (
                  <div key={x.l} style={{
                    background: 'rgba(255,255,255,0.01)',
                    border: '1px solid rgba(251,191,36,0.18)',
                    borderRadius: 4,
                    padding: '10px 8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 900, color: x.c, lineHeight: 1.1 }}>{x.v.toLocaleString('pt-BR')}</div>
                    <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4, fontWeight: 700 }}>{x.l}</div>
                  </div>
                ))}
              </div>

              {/* Botão Toggling de Seleção */}
              {(() => {
                const isSel = selecionados.has(focusedEscola.id) || (escolas.length === 0 && sugestoesSel.has(focusedEscola.id.replace('inep-', '')));
                return (
                  <button
                    onClick={() => {
                      if (escolas.length === 0) {
                        const inepCode = focusedEscola.id.replace('inep-', '');
                        setSugestoesSel(prev => {
                          const next = new Set(prev);
                          next.has(inepCode) ? next.delete(inepCode) : next.add(inepCode);
                          return next;
                        });
                      } else {
                        onToggle(focusedEscola);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: 4,
                      border: 'none',
                      borderTop: '1px solid rgba(255,255,255,0.3)',
                      borderBottom: isSel ? '3px solid #7f1d1d' : '3px solid #047857',
                      background: isSel ? 'linear-gradient(135deg, #ef4444, #991b1b)' : 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      boxShadow: isSel ? '0 4px 10px rgba(239,68,68,0.2)' : '0 4px 10px rgba(16,185,129,0.2)',
                      marginTop: 'auto'
                    }}
                  >
                    <span>{isSel ? '➖ Remover da Simulação' : '✓ Incluir na Simulação'}</span>
                  </button>
                );
              })()}
            </>
          )}
        </div>

        {/* Mapa Leaflet - Começa ocupando 100% do espaço (Claro e Limpo) */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative', zIndex: 1 }}>
          <div style={{ position: 'absolute', inset: 0 }}>
            <UnifiedMapView 
              dataPoints={dataPointsForMap}
              config={{
                initialZoom: 13,
                useClusteringByDefault: true,
                showRadiusByDefault: true,
                enableToolbar: true, // Toolbar flutuante com camadas (Alunos, Casinhas, etc.)
                theme: 'light' // TILELAYER CLARA E LIMPA (Voyager)
              }}
              onMarkerClick={handleMarkerClick}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>

      </div>

      {/* Barra Horizontal Inferior de Cadastro Customizado (Sempre visível se já houver escolas no banco) */}
      {temEscolasNoBanco && !progresso && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.02)', 
          border: '1.2px solid rgba(251, 191, 36, 0.25)', 
          borderRadius: 4,
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          zIndex: 2,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, width: '100%' }}>
            
            {/* Slider Alunos */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', minWidth: 50, fontWeight: 700 }}>Alunos:</span>
              <input type="range" min={50} max={2000} step={10} value={novaAlunos}
                onChange={e => setNovaAlunos(+e.target.value)}
                style={{ flex: 1, accentColor: '#fbbf24', cursor: 'pointer', height: 4 }} />
              <span style={{ fontSize: 12, fontWeight: 800, color: '#fbbf24', minWidth: 40 }}>{novaAlunos}</span>
            </div>

            {/* Slider Histórico */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', minWidth: 50, fontWeight: 700 }}>Histórico:</span>
              <input type="range" min={1} max={50} step={1} value={escolaAnos}
                onChange={e => setEscolaAnos(+e.target.value)}
                style={{ flex: 1, accentColor: '#fbbf24', cursor: 'pointer', height: 4 }} />
              <span style={{ fontSize: 12, fontWeight: 800, color: '#fbbf24', minWidth: 35 }}>{escolaAnos}a</span>
            </div>

            {/* Botão de Criação Azul Premium */}
            <button 
              onClick={handleCriarNova}
              onMouseEnter={() => setCreateHovered(true)}
              onMouseLeave={() => setCreateHovered(false)}
              style={{
                padding: '10px 18px',
                borderRadius: 4,
                border: 'none',
                borderTop: '1px solid rgba(255,255,255,0.3)',
                borderBottom: createHovered ? '5px solid #1e3a8a' : '3px solid #1e3a8a',
                background: createHovered ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                color: '#fff',
                fontSize: 12,
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                cursor: 'pointer',
                transform: createHovered ? 'translateY(-2px)' : 'translateY(0)',
                boxShadow: createHovered ? '0 8px 18px rgba(29, 78, 216, 0.45)' : '0 4px 10px rgba(29, 78, 216, 0.3)',
                transition: 'all 0.15s ease',
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              {createHovered && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4) 50%, transparent)',
                  animation: 'goldSweep 1.6s ease-in-out infinite',
                  pointerEvents: 'none',
                  zIndex: 1
                }} />
              )}
              <span style={{ fontSize: 16, fontWeight: 900, color: '#fff', lineHeight: 1, zIndex: 2 }}>+</span>
              <span style={{ zIndex: 2 }}>Criar</span>
            </button>
          </div>
        </div>
      )}



      {/* Overlay de Progresso Geral da Simulação */}
      {progresso && progresso.ativo && (
        <div style={{ 
          position: 'fixed', 
          inset: 0, 
          zIndex: 9999, 
          background: 'rgba(5, 2, 20, 0.90)', 
          backdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          color: '#ffffff',
          fontFamily: 'sans-serif'
        }}>
          {/* Brasão Animado */}
          <div style={{
            width: 90,
            height: 90,
            borderRadius: '50%',
            background: 'rgba(251, 191, 36, 0.05)',
            border: '2px solid rgba(251, 191, 36, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
            animation: 'corePulse 2s ease-in-out infinite',
            boxShadow: '0 8px 32px rgba(251, 191, 36, 0.15)'
          }}>
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5-10 5z" />
              <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
              <path d="M12 22v-7" />
            </svg>
          </div>

          {/* Título Principal */}
          <h2 style={{
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            textAlign: 'center',
            margin: '0 0 8px 0',
            background: 'linear-gradient(135deg, #ffe066 0%, #fbbf24 60%, #f59e0b 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
          }}>
            {progresso.finalizado
              ? (progresso.sucesso ? 'CONCLUÍDO COM SUCESSO!' : 'FALHA NA GERAÇÃO')
              : progresso.modo === 'lote'
                ? 'CRIANDO ESCOLAS DA REDE'
                : 'MODELANDO REDE ESCOLAR'}
          </h2>

          {progresso.modo === 'lote' && !progresso.finalizado && typeof progresso.atual === 'number' && typeof progresso.total === 'number' && (
            <div style={{
              fontSize: 11,
              fontWeight: 800,
              color: '#fbbf24',
              letterSpacing: '0.12em',
              marginBottom: 8,
              textTransform: 'uppercase',
            }}>
              Escola {progresso.atual} de {progresso.total}
            </div>
          )}

          {/* Subtítulo dinâmico */}
          <p style={{
            fontSize: 12,
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: 460,
            textAlign: 'center',
            margin: '0 0 24px 0',
            lineHeight: 1.5,
            fontWeight: 500
          }}>
            {progresso.finalizado
              ? (progresso.sucesso
                ? (progresso.modo === 'lote'
                  ? 'Escolas criadas. Em seguida a sessão de simulação será iniciada.'
                  : 'A estrutura escolar e os dados de simulação foram injetados no sistema.')
                : 'Ocorreu um erro no processamento. Veja o console de log.')
              : progresso.modo === 'lote'
                ? `Gerando base demo: "${progresso.nome}". Isso pode levar alguns minutos por escola.`
                : `Estamos gerando e injetando a base fictícia completa da escola demo: "${progresso.nome.toUpperCase()}".`}
          </p>

          {/* Barra de Progresso */}
          <div style={{
            width: '100%',
            maxWidth: '460px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1.2px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '4px',
            boxSizing: 'border-box',
            marginBottom: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              width: `${progresso.pct}%`,
              height: '16px',
              background: 'linear-gradient(90deg, #10b981, #059669)',
              borderRadius: '8px',
              transition: 'width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '6px',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: 9, fontWeight: 900, color: '#ffffff', letterSpacing: '0.02em' }}>
                {progresso.pct}%
              </span>
            </div>
          </div>

          {/* Grid estimado — só no seed unitário (no lote o log já acompanha cada escola) */}
          {!progresso.finalizado && progresso.modo !== 'lote' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px',
              width: '100%',
              maxWidth: '460px',
              marginBottom: 24,
              animation: 'fadeUp 0.3s ease'
            }}>
              {[
                { l: 'ALUNOS', v: novaAlunos, c: '#4ade80' },
                { l: 'TURMAS', v: calc(novaAlunos).turmas, c: '#60a5fa' },
                { l: 'PROFESSORES', v: calc(novaAlunos).profs, c: '#f472b6' },
                { l: 'RESPONSÁVEIS', v: calc(novaAlunos).resps, c: '#fbbf24' }
              ].map(x => (
                <div key={x.l} style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '6px',
                  padding: '8px 4px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: x.c }}>{x.v}</div>
                  <div style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2, fontWeight: 700 }}>{x.l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Console de Log Escuro de Injeção */}
          <div style={{
            width: '100%',
            maxWidth: '460px',
            background: 'rgba(5, 2, 20, 0.94)',
            border: '1px solid rgba(251, 191, 36, 0.25)',
            borderRadius: '10px',
            padding: '12px',
            boxSizing: 'border-box',
            fontFamily: 'monospace',
            fontSize: '9.5px',
            lineHeight: 1.5,
            color: '#fbbf24',
            height: '140px',
            overflowY: 'auto',
            textAlign: 'left',
            boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.8)'
          }} className="premium-scroll">
            {progresso.logLines.map((line, idx) => (
              <div key={idx} style={{
                color: line.startsWith('✅') ? '#4ade80' : (line.startsWith('❌') ? '#f87171' : '#fbbf24'),
                marginBottom: 3
              }}>
                {line}
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}
